/* eslint-disable camelcase */
/**
 * Submit-time half of `useOrderFormTpsl`: attach the ticked TP/SL to the order
 * that was just placed. Split out for file size; the rules are unchanged except
 * two fixes:
 *
 * - The legs close the ORDER's size, not the whole position (#22b). They used
 *   to be sent as "close everything", so a TP/SL added with a small order would
 *   close a large existing position when triggered.
 * - If the create fails after live legs were cancelled, the position has no
 *   TP/SL at all (#22c). That is reported in the persistent error dialog with a
 *   "Set TP/SL again" action that retries the same request.
 */
import { useCallback, useContext } from "react";
import { MarginTypeResponse, SideResponse } from "../../codegen-api";
import { ErrorDialogContext } from "../../contexts/ErrorDialogContext";
import { normalizeDecimalString } from "../../utils/decimalString";
import { isTpslLegsRemovedError } from "./tpsl.errors";
import { TpslDesiredState } from "./tpsl.types";

export const SET_TPSL_AGAIN = "Set TP/SL again";

export interface AttachRequest {
  instrumentName: string;
  instrumentId: string;
  side: SideResponse | string;
  amount: string | number;
  marginType: MarginTypeResponse;
  tpTrigger?: string;
  slTrigger?: string;
}

type CreateTpsl = (position: any, desired: TpslDesiredState) => Promise<unknown>;
type ErrorToast = (header: JSX.Element, message: string) => void;

/** The position-shaped description of the just-placed order, and what to set on it. */
export const describeAttach = (req: AttachRequest) => {
  // Plain decimal: `String(5e-7)` is "5e-7".
  const amount = normalizeDecimalString(req.amount) ?? String(req.amount);
  return {
    position: {
      instrument_name: req.instrumentName,
      instrument_id: req.instrumentId,
      side: req.side,
      margin_type: req.marginType,
      amount,
    },
    desired: {
      takeProfitPrice: req.tpTrigger ? Number(req.tpTrigger) : undefined,
      stopLossPrice: req.slTrigger ? Number(req.slTrigger) : undefined,
      // Close what this order adds, never "the whole position".
      //
      // OPEN GAP, deliberately not "fixed" here -- see docs/TPSL-SIZING-QUESTION.md.
      // This is the ORDER's size, and the legs are created immediately after the
      // order is accepted (usePerpsTradeFormSubmit), not when it fills. A
      // PARTIALLY filled order therefore leaves a trigger LARGER than the
      // position held.
      //
      // Neither size available here is correct:
      //   - the order's size (this line) is too big on a partial fill;
      //   - omitting it means CLOSE_FULL_POSITION ("0", useTpslForPosition.ts),
      //     which is too big whenever a prior position exists -- that was #22b,
      //     the bug this line fixed. So "0" is NOT a safe substitute.
      // The right size is "what this order actually added", known only after a
      // fill, and waiting for one was rejected on purpose: a resting limit order
      // may never produce a position at all (see useOrderFormTpsl.tsx).
      //
      // Whether this is harmful turns on ONE contract fact: is the executor's
      // close reduce-only, or a plain netted market order? Reduce-only (or a
      // clamp) makes the excess harmless; a revert means TP/SL silently fails to
      // protect; a netted oversized close would leave a residual REVERSE
      // position. Our repos contain second-hand evidence for both answers and
      // they disagree -- backend docs/tpsl.md calls it reduce-only, while
      // marketmaker's cleanup.rs, citing CLOBPerpetualRouter.sol, describes a
      // netted opposite-side market order. Neither is the contract source.
      sizeInTokens: amount,
    } as TpslDesiredState,
  };
};

/**
 * Returns `send(req)`: resolves true when the triggers were created, false when
 * the failure was reported. Never throws — the order itself already succeeded.
 */
export function useSendOrderTpsl(createTPSLOrders: CreateTpsl, addErrorToast: ErrorToast) {
  const { showError } = useContext(ErrorDialogContext);

  const send = useCallback(
    (first: AttachRequest): Promise<boolean> => {
      const attempt = async (req: AttachRequest): Promise<boolean> => {
        const { position, desired } = describeAttach(req);
        try {
          await createTPSLOrders(position, desired);
          return true;
        } catch (error: any) {
          if (isTpslLegsRemovedError(error)) {
            showError("TP/SL removed — not replaced", error.message, {
              label: SET_TPSL_AGAIN,
              onClick: () => {
                void attempt(req);
              },
            });
            return false;
          }
          const legs = [
            req.tpTrigger ? `TP ${req.tpTrigger}` : "",
            req.slTrigger ? `SL ${req.slTrigger}` : "",
          ]
            .filter(Boolean)
            .join(" · ");
          // addErrorToast decodes contract errors and translates apiErrors codes
          // itself, so hand it the raw message rather than pre-formatting.
          addErrorToast(
            <p>{`Order placed — TP/SL NOT set (${legs})`}</p>,
            error?.message || "unknown error"
          );
          return false;
        }
      };
      return attempt(first);
    },
    [addErrorToast, createTPSLOrders, showError]
  );

  return send;
}
